/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { type gestureTypes, useContextMenu } from '#hooks/PRIVATE_useVisContextMenu';
import type {
  PictoChartContextMenuContext,
  PictoChartContextMenuConfig,
  ItemInfo
} from './pictoChart.type';

type Props<D> = {
  focusedItemInfo: ItemInfo;
  items: D[];
  rootRef: RefObject<HTMLDivElement>;
  contextMenuConfig?: PictoChartContextMenuConfig<D>;
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
  getItemInfo: (element: HTMLElement) =>
    | {
        idx: number;
      }
    | undefined;
};
export const usePictoChartContextMenu = <D>({
  rootRef,
  contextMenuConfig,
  focusedItemInfo,
  items,
  onContextMenuDismissed,
  getItemInfo
}: Props<D>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<PictoChartContextMenuContext<D>>({
    onContextMenuHandler: ({ gesture, target }) => {
      if (gesture === 'keyboard') {
        const idx = focusedItemInfo.idx;
        const elem = rootRef.current?.querySelector(`[data-idx="${idx}"]`) || null;
        const data = items[idx];

        return {
          type: 'element',
          context: {
            type: 'item' as const,
            data
          },
          elem
        };
      }

      const idx = getItemInfo(target as HTMLElement)?.idx;
      const data = idx !== undefined ? items[idx] : null;
      if (data) {
        return {
          type: 'pointer',
          context: {
            type: 'item' as const,
            data
          }
        };
      }

      return {
        type: 'pointer',
        context: { type: 'background' as const }
      };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig //If there is no context menu renderer we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
